import { useCallback, useEffect, useRef, useState } from 'react';
import { FCW } from '../../types.ts';
import { ConfigSlideId, Slide } from '@/eventConfig/types.ts';
import { isNil } from 'ramda';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
import { Navigation } from './NavigationContex.ts';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { PageViews } from '@piwikpro/react-piwik-pro';

interface NavControl {
  hasPrev: boolean;
  hasNext: boolean;
  hasCancel: boolean;
  hasInfo: boolean;
  isValid: boolean;
  isNextButtonVisible: boolean;
}

interface TimelineItem {
  slide: ConfigSlideId;
  start: moment.Moment;
  finish: moment.Moment;
  duration: number;
}

export interface NavigationApi {
  currentSlide: Slide | null;
  navControl: NavControl;
  next: () => void;
  skipToNext: () => void;
  prev: () => void;
  reset: () => void;
  setSlideValid: (v: boolean) => void;
  getTimeline: () => TimelineItem[];
  gotoSlide: (slideId: ConfigSlideId) => void;
  progress: number;
}

const scrollTop = () => {
  /** scroll to top in case the previous slide was longer than 100vh */
  window.setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};

export const NavigationProvider: FCW = ({ children }) => {
  const START_SLIDE_ID = 'home';
  const END_SLIDE_ID = 'reset';
  const { eventConfig } = useEventConfig();
  const { closeLayer } = useLayer();

  const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);
  const history = useRef<ConfigSlideId[]>([]);
  const slideStartTimer = useRef<moment.Moment>(null);
  const timeline = useRef<TimelineItem[]>([]);

  const slideIdsOrdered = useRef<ConfigSlideId[]>([]);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    const currentProgress = slideIdsOrdered.current.findIndex((value) => {
      return value == currentSlide?.id;
    });
    setProgress(currentProgress / (slideIdsOrdered.current.length - 1));

    // tracking virtual page views
    PageViews.trackPageView(currentSlide?.id);
  }, [currentSlide]);

  useEffect(() => {
    if (eventConfig?.slides[START_SLIDE_ID]) {
      const slideIds: ConfigSlideId[] = calculateSlideIdsOrdered(eventConfig?.slides[START_SLIDE_ID].id);
      slideIdsOrdered.current = slideIds;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventConfig?.slides]);

  const calculateSlideIdsOrdered = (slideId: ConfigSlideId): ConfigSlideId[] => {
    const nextPage = eventConfig?.slides[slideId].next.success;

    if (nextPage && nextPage != END_SLIDE_ID) {
      return [slideId, ...calculateSlideIdsOrdered(nextPage)];
    } else {
      return [slideId];
    }
  };

  useEffect(() => {
    slideStartTimer.current = moment();
  }, []);

  const [navControl, setNavControl] = useState<NavControl>({
    hasPrev: false,
    hasNext: false,
    hasCancel: false,
    hasInfo: false,
    isNextButtonVisible: false,
    isValid: false,
  });
  const navControlRef = useRef(navControl);

  useEffect(() => {
    navControlRef.current = navControl;
  }, [navControl]);

  const updateNavControl = (slide: Slide) => {
    if (!slide) {
      return;
    }
    const nextNavControl = {
      hasPrev: slide.prev !== false && history.current.length > 0,
      hasNext: !!slide.next,
      hasCancel: slide.cancel !== false,
      hasInfo: !!slide.info,
      isNextButtonVisible: slide.isNextButtonVisible !== false,
      isValid: slide.valid || false,
    };

    navControlRef.current = nextNavControl;
    setNavControl(nextNavControl);
  };

  useEffect(() => {
    if (!isNil(eventConfig)) {
      setCurrentSlide(eventConfig.slides[START_SLIDE_ID]);
      updateNavControl(eventConfig.slides[START_SLIDE_ID]);
    }
  }, [eventConfig]);

  const setSlideValid = (isValid: boolean) => {
    navControlRef.current = {
      ...navControlRef.current,
      isValid,
    };
    setNavControl((v) => ({
      ...v,
      isValid: isValid,
    }));
  };

  const gotoSlide = useCallback(
    (slideId: ConfigSlideId) => {
      const nextSlide = eventConfig!.slides[slideId];
      closeLayer();
      updateNavControl(nextSlide);
      setCurrentSlide(nextSlide);
      history.current = [...history.current, slideId];
      scrollTop();

      const now = moment();
      timeline.current = [
        ...timeline.current,
        {
          slide: slideId,
          start: slideStartTimer.current!.clone(),
          finish: now,
          duration: now.diff(slideStartTimer.current!, 'seconds'),
        },
      ];

      slideStartTimer.current = moment();
      /** update Sentry scope */
      Sentry.setTag('slide', slideId);
    },
    [closeLayer, eventConfig]
  );

  const reset = useCallback(() => {
    slideStartTimer.current = moment();
    history.current = [];
    timeline.current = [];
    gotoSlide(START_SLIDE_ID);
  }, [gotoSlide]);

  const nextSuccess = currentSlide?.next?.success;
  const hasCurrentSlide = Boolean(currentSlide);

  const next = useCallback((): void => {
    const isValid = navControlRef.current.isValid;

    if (!isValid || !hasCurrentSlide || !nextSuccess) return;

    if (nextSuccess === 'reset') {
      reset();
    } else {
      gotoSlide(nextSuccess);
    }
  }, [hasCurrentSlide, nextSuccess, reset, gotoSlide]);

  // No "is slide valid check"
  const skipToNext = () => {
    if (!currentSlide?.next || !currentSlide) {
      return;
    }
    const nextSlideId = currentSlide.next.success;
    if (nextSlideId === 'reset') {
      reset();
    } else {
      gotoSlide(nextSlideId);
    }
  };

  const prev = useCallback(() => {
    if (!currentSlide) {
      console.error('Cannot go back: No active slide found');
      return;
    }
    if (!navControl.hasPrev) {
      console.error('Cannot go back');
      return;
    }

    // if prev is defined as being a specific slide
    if (currentSlide.prev !== undefined && currentSlide.prev !== false) {
      /** support special nav target "reset" in config (next: reset) */
      if (currentSlide.prev === 'reset') {
        reset();
      } else {
        const prevSlideId = currentSlide.prev;
        gotoSlide(prevSlideId);
      }
    } else if (history.current.length) {
      // Would like to use .at(-2) but people still use safari 14...
      const lastSlide = history.current[history.current.length - 2];
      history.current = [...history.current.slice(0, history.current.length - 2)];
      if (lastSlide) {
        gotoSlide(lastSlide);
      }
    }
  }, [currentSlide, gotoSlide, reset, navControl.hasPrev]);

  // Enable Browser-Back-Button functionality
  useEffect(() => {
    const navigateBack = () => {
      prev();
      // Pushing '/' back onto the state for browser compatability.
      window.history.pushState(null, 'unused_legacy_parameter', window.location.href);
    };
    window.addEventListener('popstate', navigateBack);
    return () => {
      window.removeEventListener('popstate', navigateBack);
    };
  }, [prev]);

  // Set CSS variable for keyboard inset so CSS can avoid overlap with soft keyboard
  useEffect(() => {
    const vv: any = (window as any).visualViewport;
    const setVar = (v: number) => {
      document.documentElement.style.setProperty('--keyboard-inset', `${Math.max(0, Math.round(v))}px`);
    };
    if (!vv) {
      setVar(0);
      return;
    }
    const updateInset = () => {
      try {
        const keyboard = Math.max(0, window.innerHeight - (vv.height + (vv.offsetTop || 0)));
        setVar(keyboard);
      } catch (_e) {
        setVar(0);
      }
    };
    updateInset();
    vv.addEventListener('resize', updateInset);
    vv.addEventListener('scroll', updateInset);
    window.addEventListener('orientationchange', updateInset);
    window.addEventListener('focusin', updateInset);
    window.addEventListener('focusout', updateInset);
    return () => {
      vv.removeEventListener('resize', updateInset);
      vv.removeEventListener('scroll', updateInset);
      window.removeEventListener('orientationchange', updateInset);
      window.removeEventListener('focusin', updateInset);
      window.removeEventListener('focusout', updateInset);
      setVar(0);
    };
  }, []);

  const getTimeline = () => {
    return timeline.current;
  };

  return (
    <Navigation.Provider
      value={{
        currentSlide,
        navControl,
        next,
        prev,
        setSlideValid,
        reset,
        getTimeline,
        gotoSlide,
        progress,
        skipToNext,
      }}
    >
      {children}
    </Navigation.Provider>
  );
};
